/**
 * itemLabels.jsx
 * Zip Perde / Sky Zip sipariş kalemleri için barkodlu etiket modülü.
 * Etiket boyutu değişince tüm içerik (font, barkod, satır aralığı) otomatik ölçeklenir.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import JsBarcode from "jsbarcode";
import {
  X, Printer, Download, Tag, Settings2, ChevronDown, ChevronUp,
  ScanLine, CheckCircle2, Package
} from "lucide-react";
import { toast } from "sonner";
import { printViaIframe } from "./utils/pdfUtils";

/* ---------- helpers ---------- */
const isZipItem = (item) => {
  const s = ((item.product || "") + (item.groupSlug || "")).toLowerCase();
  return s.includes("zip") || s.includes("sky");
};

const ono = (o) => o.orderNo || o.id || "";

const makeBarcode = (text) => {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, text, {
      format: "CODE128", displayValue: false, height: 60, margin: 0, lineColor: "#000"
    });
    return canvas.toDataURL("image/png");
  } catch { return null; }
};

/* Preset etiket boyutları */
const PRESETS = [
  { label: "40×25 mm", w: 40, h: 25 },
  { label: "50×30 mm", w: 50, h: 30 },
  { label: "60×40 mm", w: 60, h: 40, default: true },
  { label: "70×50 mm", w: 70, h: 50 },
  { label: "100×60 mm", w: 100, h: 60 },
  { label: "Özel", w: null, h: null },
];

/* ---------- tek etiket render (HTML, ölçekli) ---------- */
function LabelPreview({ item, itemNo, totalItems, order, widthMm, heightMm, scale = 1 }) {
  const barRef = useRef(null);
  const barcodeValue = `${ono(order)}-P${itemNo}`;

  useEffect(() => {
    if (!barRef.current) return;
    try {
      JsBarcode(barRef.current, barcodeValue, {
        format: "CODE128", displayValue: false,
        height: Math.max(16, heightMm * scale * 0.7),
        margin: 0, lineColor: "#000",
      });
    } catch {}
  }, [barcodeValue, heightMm, scale]);

  /* Font scale: base is 60mm wide → base font 7.5px on screen */
  const fs = (base) => `${(base * (widthMm / 60) * scale).toFixed(1)}px`;
  const W = widthMm * scale;
  const H = heightMm * scale;
  const pad = Math.max(2, 3 * scale);

  const details = [
    item.zipCesidi && `Çeşit: ${item.zipCesidi}`,
    item.fabricBrand && `Kumaş: ${item.fabricBrand}`,
    item.fabric && `${item.fabric}`,
    item.ralCode && `RAL: ${item.ralCode}`,
    item.motorLabel && `Motor: ${item.motorLabel}`,
    item.roomName && `Oda: ${item.roomName}`,
  ].filter(Boolean);

  return (
    <div
      style={{
        width: W, height: H, padding: pad, boxSizing: "border-box",
        border: "1px solid #d1d5db", borderRadius: 2 * scale,
        background: "#fff", color: "#000", overflow: "hidden",
        fontFamily: "'Arial', sans-serif", display: "flex", flexDirection: "column",
        userSelect: "none", position: "relative",
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 1.5 * scale }}>
        <span style={{ fontSize: fs(9), fontWeight: 800, color: "#E5322A", letterSpacing: -0.3 }}>ZEN</span>
        <span style={{ fontSize: fs(6.5), color: "#555" }}>{ono(order)}</span>
      </div>

      {/* MÜŞTERİ */}
      <div style={{ fontSize: fs(7.5), fontWeight: 700, lineHeight: 1.2, marginBottom: 1 * scale, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {(order.customerName || "").substring(0, 32)}
      </div>

      {/* ÜRÜN */}
      <div style={{ fontSize: fs(8), fontWeight: 800, lineHeight: 1.2, marginBottom: 1 * scale, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {(item.product || "").substring(0, 28)}
      </div>

      {/* ÖLÇÜLER — ölçüsüz kalemde "undefined" sızıntısı olmaz */}
      {(item.widthCm || item.heightCm) && (
        <div style={{ fontSize: fs(9.5), fontWeight: 900, color: "#111", marginBottom: 1 * scale }}>
          {item.widthCm > 0 ? item.widthCm : "—"} × {item.heightCm > 0 ? item.heightCm : "—"} cm
        </div>
      )}

      {/* DETAYLAR */}
      {details.length > 0 && (
        <div style={{ fontSize: fs(6.5), color: "#444", lineHeight: 1.3, marginBottom: 1 * scale, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {details.slice(0, 2).join("  ·  ")}
        </div>
      )}

      {/* KALEM + ADET */}
      <div style={{ fontSize: fs(6.5), color: "#555", marginBottom: 1 * scale }}>
        Kalem {itemNo}/{totalItems}  ·  Adet: {item.qty || 1}
      </div>

      {/* BARKOD */}
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <svg ref={barRef} style={{ maxWidth: "100%", display: "block" }} />
        <div style={{ fontSize: fs(5.5), color: "#777", marginTop: 0.5 * scale, letterSpacing: 0.3 }}>
          {barcodeValue}
        </div>
      </div>
    </div>
  );
}

/* ---------- yazdırma fonksiyonu ---------- */
async function printLabels(order, items, widthMm, heightMm, autoprint = false) {
  const filteredItems = items.filter(isZipItem);
  if (!filteredItems.length) { toast.error("Bu siparişte Zip / Sky Zip kalemi bulunamadı"); return; }

  // Barkod etiketi ayarlarını backend'den çek (varsa)
  let labelCfg = {};
  try {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("zen_token");
    const res = await fetch(`${API}/api/settings?category=document_texts`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) {
      const rows = await res.json();
      rows.forEach((r) => {
        if (r.key && r.key.startsWith("barkod_etiketi__")) {
          const field = r.key.replace("barkod_etiketi__", "");
          labelCfg[field] = r.value;
        }
      });
    }
  } catch { /* ağ hatası — varsayılan değerler kullanılır */ }

  const companyLine = labelCfg.company_line || "ZEN PERDE · Mekanik Gölgelendirme Sistemleri";
  const handlingNote = labelCfg.handling_note || "⚠ DİKKATLİ TAŞIYINIZ · KIRILABILIR";
  const footerNote   = labelCfg.footer_note   || "";
  const labelTitle   = labelCfg.title         || "KALEM ETİKETİ";

  const pages = filteredItems.map((item, idx) => {
    const barcodeValue = `${ono(order)}-P${idx + 1}`;
    const bcUrl = makeBarcode(barcodeValue);
    const details = [
      item.zipCesidi && `Çeşit: ${item.zipCesidi}`,
      item.fabricBrand && `Kumaş: ${item.fabricBrand}`,
      item.fabric && item.fabric,
      item.ralCode && `RAL: ${item.ralCode}`,
      item.motorLabel && `Motor: ${item.motorLabel}`,
      item.roomName && `Oda: ${item.roomName}`,
    ].filter(Boolean);

    return `
<div class="label">
  <div class="hdr">
    <span class="brand">ZEN</span>
    <span class="title-lbl">${labelTitle.replace(/</g,"&lt;")}</span>
    <span class="ono">${(ono(order) || "").replace(/</g,"&lt;")}</span>
  </div>
  <div class="company">${companyLine.replace(/</g,"&lt;")}</div>
  <div class="cust">${(order.customerName || "").substring(0,36).replace(/</g,"&lt;")}</div>
  <div class="prod">${(item.product || "").substring(0,32).replace(/</g,"&lt;")}</div>
  ${(item.widthCm || item.heightCm) ? `<div class="dims">${item.widthCm > 0 ? item.widthCm : "—"} × ${item.heightCm > 0 ? item.heightCm : "—"} cm</div>` : ""}
  ${details.length ? `<div class="det">${details.slice(0,2).join(" · ").replace(/</g,"&lt;")}</div>` : ""}
  <div class="kalem">Kalem ${idx+1}/${filteredItems.length} · Adet: ${item.qty || 1}</div>
  ${handlingNote ? `<div class="handling">${handlingNote.replace(/</g,"&lt;")}</div>` : ""}
  <div class="bc-wrap">
    ${bcUrl ? `<img src="${bcUrl}" class="bc-img" />` : ""}
    <div class="bc-val">${barcodeValue}</div>
  </div>
  ${footerNote ? `<div class="footer-note">${footerNote.replace(/</g,"&lt;")}</div>` : ""}
</div>`;
  }).join("\n");

  // mm → pt (1mm ≈ 2.8346pt)
  const W = widthMm, H = heightMm;
  // scale factor relative to 60mm base: fs = base * W/60
  const fs = (base) => `${(base * W / 60).toFixed(2)}pt`;

  const html = `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8">
<title>ZEN Etiket - ${ono(order)}</title>
<style>
@page { size: ${W}mm ${H}mm; margin: 0; }
*{box-sizing:border-box;margin:0;padding:0;}
body{ font-family:Arial,Helvetica,sans-serif; background:#fff; color:#000; }
.label{
  width:${W}mm; height:${H}mm;
  padding:${Math.max(1.2, W*0.035).toFixed(1)}mm;
  display:flex; flex-direction:column; overflow:hidden;
  page-break-after:always; break-after:page;
}
.label:last-child{page-break-after:auto;break-after:auto;}
.hdr{ display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:${(H*0.025).toFixed(1)}mm; border-bottom:0.4pt solid #ccc; padding-bottom:${(H*0.015).toFixed(1)}mm; }
.brand{ font-size:${fs(11)}; font-weight:900; color:#E5322A; letter-spacing:-0.3pt; }
.title-lbl{ font-size:${fs(7.5)}; font-weight:800; letter-spacing:0.5pt; text-transform:uppercase; }
.ono{ font-size:${fs(6.5)}; color:#555; }
.company{ font-size:${fs(6.5)}; color:#666; margin-bottom:${(H*0.018).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.cust{ font-size:${fs(8)}; font-weight:700; margin-bottom:${(H*0.018).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.prod{ font-size:${fs(9)}; font-weight:800; margin-bottom:${(H*0.022).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dims{ font-size:${fs(12)}; font-weight:900; margin-bottom:${(H*0.022).toFixed(1)}mm; }
.det{ font-size:${fs(7)}; color:#444; margin-bottom:${(H*0.018).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.kalem{ font-size:${fs(6.5)}; color:#555; margin-bottom:${(H*0.015).toFixed(1)}mm; }
.handling{ font-size:${fs(6)}; color:#b45309; margin-bottom:${(H*0.015).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.bc-wrap{ margin-top:auto; }
.bc-img{ max-width:100%; max-height:${(H*0.25).toFixed(1)}mm; display:block; }
.bc-val{ font-size:${fs(6)}; color:#777; letter-spacing:0.3pt; margin-top:${(H*0.01).toFixed(1)}mm; }
.footer-note{ font-size:${fs(5.5)}; color:#999; margin-top:${(H*0.01).toFixed(1)}mm; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
</style></head><body>${pages}</body></html>`;

  printViaIframe(html, false);
  toast.success(`${filteredItems.length} etiket yazıcıya gönderildi (${W}×${H}mm)`);
}

const SIZE_KEY_MAP = {
  LBL_6x4:   { w: 60,  h: 40,  idx: 2 },
  LBL_8x5:   { w: 80,  h: 50,  idx: 3 },  // 70×50'e en yakın; custom olarak 80×50 uygulanır
  LBL_10x6:  { w: 100, h: 60,  idx: 4 },
  LBL_10x15: { w: 100, h: 150, idx: 5 },   // Özel modunda
};

/* ---------- ANA MODAL ---------- */
export function ItemLabelModal({ order, onClose }) {
  const allItems = order.items || [];
  const zipItems = allItems.filter(isZipItem);
  const [widthMm, setWidthMm] = useState(60);
  const [heightMm, setHeightMm] = useState(40);
  const [customW, setCustomW] = useState("60");
  const [customH, setCustomH] = useState("40");
  const [presetIdx, setPresetIdx] = useState(2); // 60x40 default
  const [previewIdx, setPreviewIdx] = useState(0);
  const [showAllPreviews, setShowAllPreviews] = useState(false);

  // Ayarlardan kayıtlı varsayılan boyutu yükle
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("zen_token");
    fetch(`${API}/api/settings?category=document_texts`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.ok ? r.json() : [])
      .then((rows) => {
        const row = rows.find((r) => r.key === "barkod_etiketi__default_size");
        const sizeKey = row?.value;
        if (sizeKey && SIZE_KEY_MAP[sizeKey]) {
          const { w, h, idx } = SIZE_KEY_MAP[sizeKey];
          setWidthMm(w); setHeightMm(h);
          setCustomW(String(w)); setCustomH(String(h));
          setPresetIdx(sizeKey === "LBL_8x5" ? PRESETS.length - 1 : idx);
          // LBL_8x5: PRESETS'te tam eşleşme yok, Özel modunu kullan
        }
      })
      .catch(() => {});
  }, []);

  // scale önizleme için: max 300px wide
  const PREVIEW_MAX_PX = 300;
  const scale = Math.min(1, PREVIEW_MAX_PX / widthMm) * (96 / 25.4); // screen DPI

  const applyPreset = (idx) => {
    const p = PRESETS[idx];
    setPresetIdx(idx);
    if (p.w) { setWidthMm(p.w); setHeightMm(p.h); setCustomW(String(p.w)); setCustomH(String(p.h)); }
  };

  const applyCustom = () => {
    const w = Math.min(200, Math.max(30, parseInt(customW) || 60));
    const h = Math.min(150, Math.max(20, parseInt(customH) || 40));
    setWidthMm(w); setHeightMm(h);
  };

  useEffect(() => {
    if (presetIdx === PRESETS.length - 1) applyCustom();
  }, [customW, customH]);

  const previewItem = zipItems[previewIdx] || zipItems[0];

  if (!zipItems.length) return (
    <div className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-md p-6 text-center space-y-3">
        <Tag className="w-8 h-8 text-slate-400 mx-auto" />
        <p className="text-sm text-slate-500">Bu siparişte Zip Perde / Sky Zip kalemi bulunamadı.</p>
        <button onClick={onClose} className="zen-btn-ghost">Kapat</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[90] bg-black/75 flex items-center justify-center p-3 overflow-y-auto">
      <div className="zen-panel w-full max-w-2xl zen-anim-up" style={{ maxHeight: "96vh", overflow: "auto" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A] sticky top-0 bg-white dark:bg-[#131B2E] z-10">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#E5322A]" />
            <span className="font-bold text-sm">Kalem Etiketleri</span>
            <span className="zen-chip bg-[#E5322A]/10 text-[#E5322A]">{zipItems.length} kalem</span>
            <span className="zen-chip bg-slate-500/10 text-slate-500">{ono(order)}</span>
          </div>
          <button onClick={onClose} data-testid="label-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* BOYUT AYARI */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
              <Settings2 className="w-3.5 h-3.5" /> Etiket Boyutu
            </div>

            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => applyPreset(i)}
                  data-testid={`label-preset-${p.w || "custom"}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    presetIdx === i
                      ? "bg-[#E5322A] text-white border-[#E5322A]"
                      : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/40"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Özel boyut girişi */}
            {presetIdx === PRESETS.length - 1 && (
              <div className="flex items-center gap-2">
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">Genişlik (mm)</label>
                  <input
                    type="number" min="30" max="200" value={customW}
                    onChange={(e) => setCustomW(e.target.value)}
                    className="zen-input !py-1.5 !text-sm text-center w-20"
                    data-testid="label-custom-w"
                  />
                </div>
                <span className="text-slate-400 mt-4">×</span>
                <div>
                  <label className="text-[10px] text-slate-400 block mb-1">Yükseklik (mm)</label>
                  <input
                    type="number" min="20" max="150" value={customH}
                    onChange={(e) => setCustomH(e.target.value)}
                    className="zen-input !py-1.5 !text-sm text-center w-20"
                    data-testid="label-custom-h"
                  />
                </div>
                <button onClick={applyCustom} className="zen-btn-ghost !text-xs mt-4">Uygula</button>
              </div>
            )}

            <div className="text-xs text-slate-400">
              Seçili: <span className="font-bold text-slate-600 dark:text-slate-300">{widthMm} × {heightMm} mm</span>
              <span className="ml-2 text-slate-400">· Tüm font ve ölçüler otomatik ayarlanır</span>
            </div>
          </div>

          {/* ÖNIZLEME */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Önizleme</div>
              {zipItems.length > 1 && (
                <div className="flex items-center gap-1">
                  {zipItems.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPreviewIdx(i)}
                      className={`w-6 h-6 rounded text-xs transition-all ${
                        previewIdx === i
                          ? "bg-[#E5322A] text-white font-bold"
                          : "bg-slate-100 dark:bg-[#1A2540] text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-center bg-slate-100 dark:bg-[#0C1220] rounded-xl p-4" style={{ minHeight: 120 }}>
              {previewItem && (
                <LabelPreview
                  item={previewItem}
                  itemNo={previewIdx + 1}
                  totalItems={zipItems.length}
                  order={order}
                  widthMm={widthMm}
                  heightMm={heightMm}
                  scale={scale}
                />
              )}
            </div>
          </div>

          {/* TÜM ETİKETLER LİSTESİ */}
          <div className="space-y-2">
            <button
              onClick={() => setShowAllPreviews(!showAllPreviews)}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
              data-testid="label-toggle-all"
            >
              {showAllPreviews ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Tüm {zipItems.length} etiketi önizle
            </button>

            {showAllPreviews && (
              <div className="flex flex-wrap gap-3 bg-slate-50 dark:bg-[#0C1220] rounded-xl p-3">
                {zipItems.map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-1">
                    <LabelPreview
                      item={item} itemNo={i + 1} totalItems={zipItems.length}
                      order={order} widthMm={widthMm} heightMm={heightMm} scale={scale * 0.65}
                    />
                    <span className="text-[10px] text-slate-400">{i + 1}/{zipItems.length}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* KALEM ÖZETİ */}
          <div className="space-y-1.5">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Etiketlenecek Kalemler</div>
            {zipItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs rounded-lg border border-slate-200 dark:border-[#22304A] px-3 py-2">
                <ScanLine className="w-3.5 h-3.5 text-[#E5322A] shrink-0" />
                <span className="font-semibold flex-1">{item.product}</span>
                {(item.widthCm > 0 || item.heightCm > 0) && <span className="text-slate-400">{item.widthCm > 0 ? item.widthCm : "—"}×{item.heightCm > 0 ? item.heightCm : "—"}cm</span>}
                <span className="text-slate-400">×{item.qty || 1}</span>
                <code className="text-[10px] bg-slate-100 dark:bg-[#1A2540] px-1.5 py-0.5 rounded font-mono">
                  {ono(order)}-P{i + 1}
                </code>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-[#22304A] flex items-center justify-between sticky bottom-0 bg-white dark:bg-[#131B2E]">
          <span className="text-xs text-slate-400">{zipItems.length} etiket · {widthMm}×{heightMm}mm</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="zen-btn-ghost">Vazgeç</button>
            <button
              onClick={() => printLabels(order, allItems, widthMm, heightMm, true)}
              className="zen-btn-primary"
              data-testid="label-print-btn"
            >
              <Printer className="w-4 h-4" /> Yazdır ({zipItems.length} etiket)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
